import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Copy, Users, DollarSign, Clock, Gift } from "lucide-react";

export default function Referral() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/me/referrals").then((r) => setData(r.data)).catch(() => {}); }, []);

  const link = `${window.location.origin}/signup?ref=${data?.referral_code || ""}`;
  const copy = () => { navigator.clipboard.writeText(link); toast.success("Referral link copied!"); };

  const stats = [
    { icon: Users, label: "Total referrals", value: data?.total_referrals ?? 0 },
    { icon: DollarSign, label: "Earned", value: `$${data?.earned ?? 0}` },
    { icon: Clock, label: "Pending", value: `$${data?.pending ?? 0}` },
    { icon: Gift, label: "Credits", value: `$${data?.credits ?? 0}` },
  ];

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10">
        <h1 className="font-display text-4xl font-extrabold text-lawn-ink">Referral Dashboard</h1>
        <p className="text-muted-foreground mt-1">Share your link, track referrals, and earn rewards.</p>

        <div className="bg-lawn-ink rounded-2xl p-6 mt-8 text-white">
          <p className="text-sm text-white/60">Your unique referral link</p>
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <Input data-testid="referral-link" value={link} readOnly className="bg-white/10 border-white/20 text-white rounded-xl" />
            <Button data-testid="referral-copy-btn" onClick={copy} className="rounded-xl bg-lawn hover:bg-lawn-dark font-semibold gap-2 shrink-0"><Copy className="w-4 h-4" /> Copy</Button>
          </div>
          <p className="text-xs text-white/40 mt-3">Referral code: <span className="font-bold text-gold">{data?.referral_code}</span></p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          {stats.map((s, i) => (
            <div key={i} className="bg-white border border-border rounded-2xl p-5">
              <s.icon className="w-5 h-5 text-lawn mb-2" />
              <p className="font-display text-2xl font-extrabold text-lawn-ink">{s.value}</p>
              <p className="text-xs text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="bg-white border border-border rounded-2xl p-6 mt-6">
          <h3 className="font-display font-bold text-lawn-ink mb-4">Your referrals</h3>
          {data?.referrals?.length ? (
            <div className="space-y-2">
              {data.referrals.map((r) => (
                <div key={r.referral_id} className="flex items-center justify-between py-3 border-b border-border last:border-0">
                  <div>
                    <p className="text-sm font-semibold text-lawn-ink capitalize">{r.referred_role} referral</p>
                    <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-lawn">${r.reward}</p>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${r.payout_status === "paid" ? "bg-lawn-light text-lawn-dark" : "bg-gold-light text-gold-dark"}`}>{r.payout_status}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-muted-foreground text-sm">No referrals yet. Share your link to get started!</p>}
        </div>
      </div>
    </Layout>
  );
}
